/* eslint-disable react/no-array-index-key */
import { Common } from '@stockbit/ui';
import { useEffect, useMemo, useState } from 'react';
import Image from 'global/Image';
import useDebounce from 'hooks/useDebounce';
import If from 'global/ErrorHandlers/If';
import DATA_CY from 'constants/data-cy';
import useClickOutside from 'hooks/useClickOutside';
import {
  Button,
  CompanySearchResultWrapper,
  FormFooterWrapper,
  SearchContainer,
  SearchInput,
  SearchInputDivider,
  SearchWrapper,
  SkeletonItemCheckbox,
  SkeletonItemText,
  SkeletonItemWrapper,
  SkeletonWrapper,
} from './styled';
import { SelectedSymbolWrapper } from '../styled';
import StockChip from './StockChip';
import CompanySearchItem from './SearchItem';
import { searchCompanyStore, selectedSymbolStore } from './store';
import { RunningTradeStockFilter, StockFilterTypeEnum } from '../../../types';

const { Flex, Text } = Common;

interface ISearchStock {
  isAllowMultipleSelect: boolean,
  filter: RunningTradeStockFilter;
  setFilter: (value: RunningTradeStockFilter) => void;
  showResult: boolean;
  onShowSearchResult: (showResult: boolean) => void;
  onApplyStockFilter: (value: RunningTradeStockFilter) => void;
}

const SearchStock = ({
  isAllowMultipleSelect,
  filter,
  setFilter,
  showResult,
  onShowSearchResult,
  onApplyStockFilter,
}: ISearchStock) => {
  const searchCompany = searchCompanyStore((s) => s.fetch);
  const searchResult = searchCompanyStore((s) => s.data);
  const isLoading = searchCompanyStore((s) => s.isLoading);

  const selectedSymbol = selectedSymbolStore((store) => store.symbols);
  const updateSymbols = selectedSymbolStore((s) => s.updateSymbols);

  const isDisabled = filter?.type !== StockFilterTypeEnum.Custom;

  const [tempSelectedSymbol, setTempSelectedSymbol] = useState<string[]>([]);

  const [keyword, setKeyword] = useState('');

  const { ref: searchStockRef } = useClickOutside(() => {
    if (showResult && isAllowMultipleSelect) {
      const appliedSymbol = [...selectedSymbol, ...tempSelectedSymbol];

      updateSymbols(appliedSymbol);
      setTempSelectedSymbol([]);
      setKeyword('');

      onApplyStockFilter({
        type: StockFilterTypeEnum.Custom,
        selected: appliedSymbol,
      });
    }
  });

  const keywordDebounce = useDebounce(keyword, 500);

  useEffect(() => {
    setKeyword('');
  }, []);

  useEffect(() => {
    searchCompany(keywordDebounce, 1);
  }, [keywordDebounce]);

  const onSelectCompany = (symbol: string) => {
    if (isAllowMultipleSelect) {
      setTempSelectedSymbol((prev) => [...prev, symbol]);
    } else {
      setTempSelectedSymbol(() => [symbol]);
      onApplyStockFilter({
        type: StockFilterTypeEnum.Custom,
        selected: [symbol],
      });
    }
  };

  const onRemoveCompany = (symbol: string) => {
    if (tempSelectedSymbol.includes(symbol)) {
      const updatedSymbols = tempSelectedSymbol.filter(
        (item) => item !== symbol,
      );
      setTempSelectedSymbol(updatedSymbols);
    }
    if (selectedSymbol.includes(symbol)) {
      const updatedSymbols = selectedSymbol.filter((item) => item !== symbol);
      updateSymbols(updatedSymbols);
    }
  };

  const handlePressEnter = (e) => {
    const findSymbol = searchResult
      .find((item) => item.symbol_2?.toUpperCase() === e.target.value?.toUpperCase());

    if (findSymbol && isAllowMultipleSelect) {
      const symbolCombined = `${findSymbol.symbol_2}#${findSymbol.name}`;
      const isAlreadyAdded = selectedSymbol.includes(symbolCombined);

      if (!isAlreadyAdded) {
        const appliedSymbol = [...selectedSymbol, symbolCombined];

        onApplyStockFilter({
          type: StockFilterTypeEnum.Custom,
          selected: appliedSymbol,
        });
        updateSymbols(appliedSymbol);
        setTempSelectedSymbol([]);
      }
    } else if (findSymbol && !isAllowMultipleSelect) {
      const symbolCombined = `${findSymbol.symbol_2}#${findSymbol.name}`;
      onApplyStockFilter({
        type: StockFilterTypeEnum.Custom,
        selected: [symbolCombined],
      });
      updateSymbols([symbolCombined]);
      setTempSelectedSymbol([]);
    }
  };

  const onClickAdd = () => {
    const appliedSymbol = [...selectedSymbol, ...tempSelectedSymbol];

    updateSymbols(appliedSymbol);
    setTempSelectedSymbol([]);
    setKeyword('');

    onShowSearchResult(false);

    onApplyStockFilter({
      type: StockFilterTypeEnum.Custom,
      selected: appliedSymbol,
    });
  };

  const onClickCancel = () => {
    onShowSearchResult(false);
    setTempSelectedSymbol([]);
  };

  const isEmpty = useMemo(() => {
    if (searchResult.length === 0 && keyword.length > 1) {
      return true;
    }
    return false;
  }, [isLoading, searchResult, keyword]);

  useEffect(() => {
    setFilter({
      type: StockFilterTypeEnum.Custom,
      selected: selectedSymbol,
    });
  }, [selectedSymbol]);

  useEffect(() => {
    // reset selected symbol if user remove symbol but not apply the filter
    if (
      filter.type === StockFilterTypeEnum.Custom
      && filter.selected !== selectedSymbol
    ) {
      updateSymbols(filter.selected);
    }
  }, [filter]);

  const showSelectedSymbolChip = isAllowMultipleSelect
    && !showResult
    && selectedSymbol.length > 0;

  return (
    <div
      ref={searchStockRef}
      style={{ height: showResult ? '100%' : 'auto' }}
    >
      <SearchWrapper>
        <SearchContainer
          onClick={() => {
            setFilter({
              type: StockFilterTypeEnum.Custom,
              selected: selectedSymbol,
            });
          }}
        >
          <Flex>
            <SearchInput
              prefix={
                <Image
                  src="/icon/search-rounded.svg"
                  alt="icon"
                  width="14px"
                  height="13px"
                />
              }
              placeholder="Search Stocks"
              value={keyword}
              onChange={(e) => {
                onShowSearchResult(true);
                setKeyword(e.target.value);
              }}
              onPressEnter={handlePressEnter}
              data-cy={DATA_CY.RUNNING_TRADE.SEARCH_INPUT}
            />
          </Flex>
          {showSelectedSymbolChip && (
            <>
              <SearchInputDivider />
              <SelectedSymbolWrapper isDisabled={isDisabled}>
                {selectedSymbol.map((item) => (
                  <StockChip
                    key={item}
                    symbol={item.split('#')[0]}
                    onRemove={() => onRemoveCompany(item)}
                  />
                ))}
              </SelectedSymbolWrapper>
            </>
          )}
        </SearchContainer>
        {selectedSymbol.length >= 25 && (
          <Text size="12px" color="#858585">
            *Maximum 25 Stocks
          </Text>
        )}
      </SearchWrapper>

      <div style={{ display: showResult ? 'block' : 'none' }}>
        {isLoading && (
          <SkeletonWrapper>
            {[...Array(4)].map((___, i) => (
              <SkeletonItemWrapper key={i}>
                <SkeletonItemText />
                <SkeletonItemCheckbox>
                  <div className="checkbox" />
                </SkeletonItemCheckbox>
              </SkeletonItemWrapper>
            ))}
          </SkeletonWrapper>
        )}

        <If condition={!isLoading}>
          <If condition={isEmpty}>
            <Flex
              align="center"
              justify="center"
              direction="column"
              height="410px"
            >
              <Image src="/illustration/empty-search-running-trade.svg" />
              <Text mt="16px" size="13px" color="#858585">
                No result found
              </Text>
            </Flex>
          </If>
          <If condition={!isEmpty}>
            <CompanySearchResultWrapper>
              {searchResult.map((item) => (
                <CompanySearchItem
                  {...item}
                  key={item.id}
                  onSelect={onSelectCompany}
                  onRemove={onRemoveCompany}
                  isSelected={
                    selectedSymbol.includes(`${item.symbol_2}#${item.name}`) ||
                    tempSelectedSymbol.includes(`${item.symbol_2}#${item.name}`)
                  }
                  isDisabled={
                    selectedSymbol.length + tempSelectedSymbol.length >= 25
                  }
                  hasCheckbox={isAllowMultipleSelect}
                />
              ))}
            </CompanySearchResultWrapper>
          </If>
        </If>

        {isAllowMultipleSelect && (
          <FormFooterWrapper>
            <Button onClick={onClickCancel} style={{ color: '#858585' }}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={onClickAdd}
              disabled={tempSelectedSymbol.length === 0}
            >
              Add
            </Button>
          </FormFooterWrapper>
        )}
      </div>
    </div>
  );
};

export default SearchStock;
